// File: frontend/src/components/NavBar.jsx

/*
  Primary navigation: Home, flywheel sections A–D, and More.
  A section is omitted when the viewer cannot open anything in it.
  More holds Admin and Help, outside the wheel.
*/

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import {
  HELP_LINKS,
  NAV_CONFIG,
  visibleFlywheelSections,
  visibleMoreAreas,
  visibleTools,
  toolLinkLabel,
} from '../config/permissions';
import { findRouteForPathname } from '../hooks/useRoutePermission';
import './NavBar.css';

const iconSize = 20;

const HomeIcon = () => (
  <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const MoreIcon = () => (
  <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </svg>
);

function pathIsInArea(pathname, area) {
  if (!area?.path) return false;
  return pathname === area.path || pathname.startsWith(`${area.path}/`);
}

const NavBar = () => {
  const { hasPermission, isAdmin } = usePermissions();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreBtnRef = useRef(null);
  const sheetRef = useRef(null);

  const access = { hasPermission, isAdmin, hasAssignedCheckup: false };
  const visibleIds = new Set(visibleFlywheelSections(access).map((section) => section.id));
  const items = NAV_CONFIG.filter((item) => item.id === 'home' || item.id === 'more' || visibleIds.has(item.id));
  const moreAreas = visibleMoreAreas(access);
  const currentRoute = findRouteForPathname(location.pathname);
  const moreIsCurrent = moreAreas.some((area) => (
    pathIsInArea(location.pathname, area) || currentRoute?.section === area.id
  ));

  const closeMore = useCallback(() => {
    setMoreOpen(false);
    requestAnimationFrame(() => {
      moreBtnRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const sheet = sheetRef.current;
    if (!sheet) return undefined;
    const focusables = Array.from(sheet.querySelectorAll('a[href], button:not([disabled])'));
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMore();
        return;
      }
      if (event.key !== 'Tab' || focusables.length === 0) return;
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [moreOpen, closeMore]);

  const itemIsCurrent = (item) => {
    if (item.id === 'home') return location.pathname === '/';
    if (item.id === 'more') return moreIsCurrent;
    if (pathIsInArea(location.pathname, item)) return true;
    return currentRoute?.section === item.id;
  };

  return (
    <>
      <nav className="navbar" aria-label="Primary">
        {items.map((item) => {
          if (item.id === 'more') {
            return (
              <button
                key="more"
                ref={moreBtnRef}
                type="button"
                id="nav-more-button"
                className={`nav-item nav-more-btn ${moreOpen || moreIsCurrent ? 'active' : ''}`}
                onClick={() => setMoreOpen((open) => !open)}
                aria-label="More"
                aria-expanded={moreOpen}
                aria-controls="nav-more-sheet"
              >
                <span className="nav-icon"><MoreIcon /></span>
                <span className="nav-label">More</span>
              </button>
            );
          }

          const current = itemIsCurrent(item);
          return (
            <Link
              key={item.id}
              to={item.path}
              className={`nav-item${current ? ' active' : ''}`}
              aria-current={current ? 'page' : undefined}
              aria-label={item.letter ? `${item.letter}, ${item.title}` : item.label}
            >
              {item.letter ? (
                <span className="nav-letter" aria-hidden="true">{item.letter}</span>
              ) : (
                <span className="nav-icon"><HomeIcon /></span>
              )}
              <span className="nav-label">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {moreOpen && (
        <>
          <div
            className="nav-more-backdrop"
            onClick={closeMore}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                closeMore();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
          <div
            ref={sheetRef}
            id="nav-more-sheet"
            className="nav-more-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="More"
          >
            <div className="nav-more-handle" aria-hidden="true" />
            {moreAreas.map((area) => {
              const links = area.id === 'help'
                ? HELP_LINKS.map((link) => ({ path: link.path, label: link.label }))
                : visibleTools(area.id, access).map((route) => ({
                  path: route.path,
                  label: toolLinkLabel(route),
                }));
              return (
                <div key={area.id} className="nav-more-group">
                  <Link to={area.path} className="nav-more-item" onClick={closeMore}>
                    <span className="nav-more-title">{area.navLabel}</span>
                    <span className="nav-more-sub">{area.description}</span>
                  </Link>
                  {links.map((link) => (
                    <Link key={link.path} to={link.path} className="nav-more-sublink" onClick={closeMore}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              );
            })}
            <button type="button" className="nav-more-close" onClick={closeMore}>
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default NavBar;
